import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Globe, ListChecks } from "@phosphor-icons/react"
import { Anchor, Content, Portal, Root } from "@radix-ui/react-popover"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { UserProvider } from "@/components/user-wrapper"
import { joinStrings } from "@/lib/joinString"
import { getAvatarColorByRole, getRoleName } from "@/modules/common/helpers/common-helper"
import { getLocalizedSpecialties } from "@/modules/shift-plan-overview/helpers/shift-plan-helper"
import type { useShiftPlanOverviewQuery$data } from "@/modules/shift-plan-overview/services/__generated__/useShiftPlanOverviewQuery.graphql"

type Agent = useShiftPlanOverviewQuery$data["shiftPlanOverview"][number]

type AgentTooltipProviderProps = {
  agents: Map<string, Agent>
  children: React.ReactNode
}

export const AgentTooltipProvider = (props: AgentTooltipProviderProps) => {
  const { agents, children } = props
  const wrapperRef = useRef<HTMLDivElement>(null)
  const activeUserNameRef = useRef<string | null>(null)
  const avatarRef = useRef<{ getBoundingClientRect: () => DOMRect }>({
    getBoundingClientRect: () => new DOMRect(),
  })
  const [agent, setAgent] = useState<Agent | null>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const handleMouseOver = (e: Event) => {
      if (!(e.target instanceof HTMLElement)) return

      const avatar = e.target.closest(".agent-avatar")

      if (!avatar) {
        if (activeUserNameRef.current) {
          setAgent(null)
          activeUserNameRef.current = null
        }
        return
      }

      const userName = avatar.getAttribute("data-username")
      if (!userName || userName === activeUserNameRef.current) return

      const agentData = agents.get(userName)
      if (!agentData) return

      activeUserNameRef.current = userName
      avatarRef.current = { getBoundingClientRect: () => avatar.getBoundingClientRect() }
      setAgent(agentData)
    }

    const handleMouseLeave = () => {
      setAgent(null)
      activeUserNameRef.current = null
    }

    wrapper.addEventListener("mouseover", handleMouseOver)
    wrapper.addEventListener("mouseleave", handleMouseLeave)
    return () => {
      wrapper.removeEventListener("mouseover", handleMouseOver)
      wrapper.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [agents])

  return (
    <Root open={!!agent}>
      <div ref={wrapperRef} className="shift-timeline-wrapper h-full w-full">
        {children}
        <Anchor virtualRef={avatarRef} />
        <Portal>
          <Content
            side="bottom"
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e: Event) => e.preventDefault()}
            className="bg-surface-inverted text-on-surface-inverted z-50 rounded-md px-3 py-2 shadow-lg outline-hidden">
            {agent && <AgentTooltipBody agent={agent} />}
          </Content>
        </Portal>
      </div>
    </Root>
  )
}

const AgentTooltipBody = ({ agent }: { agent: Agent }) => {
  const { t } = useTranslation()

  const languages = joinStrings(
    agent.languages.map((l) => t(`common.languageList.${l.toLowerCase()}`)),
    ", ",
  )
  const qualifications = agent.qualifications.join(", ")

  return (
    <UserProvider
      userName={agent.userName}
      render={(user) => {
        const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
        const fullName = joinStrings([user.professionalTitle, user.firstName, user.lastName], " ")
        const subtitle = joinStrings([
          t(getRoleName(user.role)),
          agent.isOperationsLead ? t("shiftPlanOverview.tooltip.operationsLead") : null,
          getLocalizedSpecialties(t, agent.specialties),
        ])

        return (
          <>
            <div className="flex items-center gap-2 py-1">
              <Avatar className="h-10 w-10 items-center justify-center">
                <AvatarFallback className={`flex text-sm font-medium ${getAvatarColorByRole(user.role)}`}>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col justify-center">
                <div className="text-sm leading-5">{fullName}</div>
                <div className="text-xs leading-4 tracking-[0.25px] opacity-70">{subtitle}</div>
              </div>
            </div>
            {languages && (
              <div className="mt-1 flex items-center gap-2 text-xs leading-4 tracking-[0.25px] opacity-70">
                <Globe className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {t("shiftPlanOverview.tooltip.languages")}: {languages}
                </span>
              </div>
            )}
            {qualifications.length > 0 && (
              <div className="mt-1 flex items-center gap-2 text-xs leading-4 tracking-[0.25px] opacity-70">
                <ListChecks className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {t("shiftPlanOverview.tooltip.qualifications")}: {qualifications}
                </span>
              </div>
            )}
          </>
        )
      }}
    />
  )
}
